import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, generateResumePlaceholderImage, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "../../common.js";
import { formatNumber } from "../../../proposalValidation.js";

export const createResumeTemp1 = async (resumeData, startPage) => {
    const style = resumeData.style || {};
    const sections = [];
    const projectsPerPage = resumeData.projectsPerPage || 6;

    const PAGE_MARGIN = 200;
    const BODY_SPACER = 600;
    const BODY_HEIGHT = 14030;
    const CONTENT_WIDTH = FULL_WIDTH - (PAGE_MARGIN * 2);
    const SIDEBAR_WIDTH_PERCENT = 30;
    const MAIN_CONTENT_WIDTH_PERCENT = 70;

    const sidebarWidthDxa = Math.floor(CONTENT_WIDTH * (SIDEBAR_WIDTH_PERCENT / 100));
    const mainWidthDxa = Math.floor(CONTENT_WIDTH * (MAIN_CONTENT_WIDTH_PERCENT / 100));

    let currentAbsPage = startPage || 1;

    for (const member of resumeData.members) {
        const memberProjects = member.projects || [];
        const numPages = Math.max(1, Math.ceil(memberProjects.length / projectsPerPage));

        for (let p = 0; p < numPages; p++) {
            const isFirst = p === 0;
            const pageProjects = memberProjects.slice(p * projectsPerPage, (p + 1) * projectsPerPage);

            // ================= SIDEBAR (LEFT) =================
            const sidebarChildren = [];

            if (isFirst) {
                // 1. Profile Picture (Always maintains height)
                let profileImgRun = null;
                const imgUrl = `https://api-dev.kbdp.kiesetechnologies.com/kbdp/${member.profilePicturePath}`;
                const profileImgData = member.profilePicturePath ?
                    await fetchImageWithDimensions(imgUrl) :
                    generateResumePlaceholderImage(sidebarWidthDxa, 3000, fixColor(style.colorScheme?.body || "EEEEEE"));

                if (profileImgData) {
                    profileImgRun = fitImage(profileImgData, sidebarWidthDxa, 3000, "cover");
                } else {
                    // Fallback to placeholder if fetch failed
                    profileImgRun = fitImage(generateResumePlaceholderImage(sidebarWidthDxa, 3000, fixColor(style.colorScheme?.body || "EEEEEE")), sidebarWidthDxa, 3000, "cover");
                }

                sidebarChildren.push(new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [new TableRow({
                        height: { value: 3000, rule: HeightRule.EXACT },
                        children: [new TableCell({
                            shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                            children: [new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: profileImgRun ? [profileImgRun] : []
                            })]
                        })]
                    })]
                }));

                // 2. Name & Role Box (Orange)
                sidebarChildren.push(new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [new TableRow({
                        children: [new TableCell({
                            shading: { fill: "E66E2D" },
                            margins: { top: 100, bottom: 100, left: 100, right: 100 },
                            children: [
                                new Paragraph({
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: (member.teamMemberName || "N/A").toUpperCase(),
                                            color: "FFFFFF",
                                            bold: true,
                                            size: 18 * SCALE,
                                            font: "sans-serif"
                                        }),
                                        new TextRun({ break: 1 }),
                                        new TextRun({
                                            text: (member.role || "N/A").toUpperCase(),
                                            color: "FFFFFF",
                                            size: 18 * SCALE,
                                            font: "sans-serif"
                                        })
                                    ]
                                })
                            ]
                        })]
                    })]
                }));

                // 3. Why Section (Wrapped in table for margins)
                sidebarChildren.push(new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [new TableRow({
                        children: [new TableCell({
                            margins: { left: 100, right: 100, top: 100, bottom: 100 },
                            children: [
                                new Paragraph({
                                    alignment: AlignmentType.CENTER,
                                    spacing: { after: 120 },
                                    children: [new TextRun({
                                        text: `Why ${member.teamMemberName.split(" ")[0]}?`,
                                        bold: style.heading?.bold || true,
                                        italics: style.heading?.italic,
                                        underline: style.heading?.underline ? {} : undefined,
                                        size: (style.heading?.size || 14) * SCALE,
                                        color: fixColor(style.heading?.color || "#000000"),
                                        font: style.heading?.font
                                    })]
                                }),
                                new Paragraph({
                                    children: [new TextRun({
                                        text: member.whyTeamMember || "N/A",
                                        size: (style.text?.size || 10) * SCALE,
                                        color: fixColor(style.text?.color || "#333333"),
                                        font: style.text?.font,
                                        bold: style.text?.bold,
                                        italics: style.text?.italic,
                                        underline: style.text?.underline ? {} : undefined,
                                    })]
                                })
                            ]
                        })]
                    })]
                }));

                const sidebarTopContent = [...sidebarChildren];
                sidebarChildren.length = 0; // Clear for re-assembly

                // 4. Experience Box (Dark Gray)
                const experienceBox = new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [new TableRow({
                        children: [new TableCell({
                            shading: { fill: "373236" },
                            margins: { top: 100, bottom: 100 },
                            children: [
                                new Paragraph({
                                    alignment: AlignmentType.CENTER,
                                    children: [new TextRun({
                                        text: `${member.yearsOfExperience || 0} YEARS EXPERIENCE`,
                                        color: "FFFFFF",
                                        bold: true,
                                        size: 12 * SCALE,
                                        font: "sans-serif"
                                    })]
                                })
                            ]
                        })]
                    })]
                });

                // Re-assemble sidebar with bottom anchoring (85/15 split)
                sidebarChildren.push(new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [
                        new TableRow({
                            height: { value: Math.floor(BODY_HEIGHT * 0.9), rule: HeightRule.EXACT },
                            children: [new TableCell({
                                verticalAlign: VerticalAlign.TOP,
                                children: sidebarTopContent
                            })]
                        }),
                        new TableRow({
                            height: { value: Math.floor(BODY_HEIGHT * 0.1), rule: HeightRule.EXACT },
                            children: [new TableCell({
                                verticalAlign: VerticalAlign.BOTTOM,
                                children: [experienceBox]
                            })]
                        })
                    ]
                }));
            } else {
                // Continuation mark or empty for secondary pages
                sidebarChildren.push(new Paragraph({
                    children: []
                }));
            }


            // ================= MAIN CONTENT (RIGHT) =================
            const experienceChildren = [];

            for (const proj of pageProjects) {
                let projImgRun = null;
                const targetPhoto = proj.uploadedPhoto || proj.photoURL;
                if (targetPhoto) {
                    const imgData = await fetchImageWithDimensions(targetPhoto);
                    if (imgData) {
                        projImgRun = fitImage(imgData, 2000, 1600, "cover");
                    }
                }

                experienceChildren.push(new Table({
                    margins: { top: 100, bottom: 100 },
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [new TableRow({
                        children: [
                            // Project Image (Always maintains height)
                            new TableCell({
                                width: { size: 2000, type: WidthType.DXA },
                                children: [new Table({
                                    width: { size: 2000, type: WidthType.DXA },
                                    borders: {
                                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                                    },
                                    rows: [new TableRow({
                                        height: { value: 1600, rule: HeightRule.EXACT },
                                        children: [new TableCell({
                                            shading: projImgRun ? undefined : { fill: "FFFFFF" },
                                            children: [new Paragraph({
                                                alignment: AlignmentType.CENTER,
                                                children: projImgRun ? [projImgRun] : []
                                            })]
                                        })]
                                    })]
                                })]
                            }),
                            // Spacing Cell
                            new TableCell({ width: { size: 200, type: WidthType.DXA }, children: [] }),
                            // Project Details
                            new TableCell({
                                width: { size: mainWidthDxa - 2200, type: WidthType.DXA },
                                children: [
                                    new Paragraph({
                                        spacing: { after: 10 },
                                        children: [
                                            // Project Name
                                            new TextRun({
                                                text: (proj.projectName || "N/A") + " ",
                                                bold: style.label?.bold || true,
                                                italics: style.label?.italic,
                                                underline: style.label?.underline ? {} : undefined,
                                                size: (style.label?.size || 11) * SCALE,
                                                color: fixColor(style.label?.color || "#000000"),
                                                font: style.label?.font
                                            }),
                                            // Size
                                            new TextRun({
                                                text: `Size: ${proj.projectSize ? formatNumber(proj.projectSize) + " SF" : "N/A"} | `,
                                                size: (style.text?.size || 10) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            // Value
                                            new TextRun({
                                                text: `Value: ${proj.value ? "$" + formatNumber(proj.value) : "N/A"} | `,
                                                size: (style.text?.size || 10) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            // Owner
                                            new TextRun({
                                                text: `Owner: ${proj.client || "N/A"} | `,
                                                size: (style.text?.size || 10) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            // Completion
                                            new TextRun({
                                                text: `Completion Date: ${proj.constructionCompletionDate ? new Date(proj.constructionCompletionDate).getFullYear() : "N/A"} | `,
                                                size: (style.text?.size || 10) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            // Scope
                                            new TextRun({
                                                text: `Scope: ${proj.projectDetails || "N/A"}`,
                                                size: (style.text?.size || 10) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })]
                }));

                experienceChildren.push(new Paragraph({ spacing: { after: 20 } }));
            }

            // ================= ASSEMBLE PAGE CONTENT =================
            const bodyContentTable = new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [new TableRow({
                    height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                    children: [
                        // Sidebar
                        new TableCell({
                            width: { size: SIDEBAR_WIDTH_PERCENT, type: WidthType.PERCENTAGE },
                            shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                            children: sidebarChildren
                        }),
                        // Spacer
                        new TableCell({ width: { size: 4, type: WidthType.PERCENTAGE }, children: [] }),
                        // Main Experiences
                        new TableCell({
                            margins: { left: 100, right: 100 },
                            width: { size: MAIN_CONTENT_WIDTH_PERCENT - 4, type: WidthType.PERCENTAGE },
                            shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                            children: experienceChildren
                        })
                    ]
                })]
            });

            // ================= PAGE STRUCTURE (Header, Body, Footer) =================
            const headerTable = createCommonHeader(resumeData.title, fixColor(style.colorScheme?.header || "#545454"));
            const footerTable = createCommonFooter(currentAbsPage, fixColor(style.colorScheme?.footer || "#545454"));

            const pageTable = new Table({
                width: { size: FULL_WIDTH, type: WidthType.DXA },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [
                    // 1. Header
                    new TableRow({ children: [new TableCell({ children: [headerTable] })] }),
                    // 2. Body Margin Top
                    new TableRow({ height: { value: BODY_SPACER, rule: HeightRule.EXACT }, children: [new TableCell({ children: [] })] }),
                    // 3. Main Content Wrapper (handling page margins)
                    new TableRow({
                        height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                        children: [new TableCell({
                            children: [new Table({
                                width: { size: 100, type: WidthType.PERCENTAGE },
                                borders: {
                                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                                },
                                rows: [new TableRow({
                                    height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                                    children: [
                                        new TableCell({ width: { size: PAGE_MARGIN, type: WidthType.DXA }, children: [] }),
                                        new TableCell({ width: { size: CONTENT_WIDTH, type: WidthType.DXA }, children: [bodyContentTable] }),
                                        new TableCell({ width: { size: PAGE_MARGIN, type: WidthType.DXA }, children: [] }),
                                    ]
                                })]
                            })]
                        })]
                    }),
                    // 4. Body Margin Bottom (filler to push footer down)
                    new TableRow({ height: { value: BODY_SPACER, rule: HeightRule.AT_LEAST }, children: [new TableCell({ children: [] })] }),
                    // 5. Footer
                    new TableRow({ children: [new TableCell({ children: [footerTable] })] })
                ]
            });

            sections.push({
                properties: { page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } } },
                children: [pageTable]
            });
            currentAbsPage++;
        }
    }

    return sections;
};
